import { useEffect, useLayoutEffect, useRef, useState, RefObject } from 'react';

interface UseStickyFlipProps {
	contentWrapperRef: RefObject<HTMLElement>;
	stickyContainerRef: RefObject<HTMLElement>;
	offset?: number;
	stickyTop?: number;
	forceSticky?: boolean; // New prop to force sticky behavior (e.g. during loading)
}

export const useStickyFlip = ({
	contentWrapperRef,
	stickyContainerRef,
	offset = 50,
	stickyTop = 0,
	forceSticky = false,
}: UseStickyFlipProps) => {
	const [isBottom, setIsBottom] = useState(false);
	const [isStuck, setIsStuck] = useState(false);
	const [isStabilizing, setIsStabilizing] = useState(false);
	const prevRectRef = useRef<DOMRect | null>(null);

	// Grace period after loading (forceSticky) to allow layout to settle
	useEffect(() => {
		if (!forceSticky) {
			setIsStabilizing(true);
			const timer = setTimeout(() => setIsStabilizing(false), 500);
			return () => clearTimeout(timer);
		}
	}, [forceSticky]);

	// 1. Scroll Listener & Resize Observer
	useEffect(() => {
		const handleScroll = () => {
			if (!contentWrapperRef.current) return;
			const contentRect = contentWrapperRef.current.getBoundingClientRect();
			const viewportHeight = window.innerHeight;

			// Check if bottom of content is visible (with buffer)
			let shouldBeBottom = contentRect.bottom < viewportHeight - offset;

			// If content is shorter than viewport (e.g. loading), keep sticky
			if (contentRect.height < viewportHeight) {
				shouldBeBottom = false;
			}

			// Force sticky if requested (e.g. loading) or during stabilization
			if (forceSticky || isStabilizing) {
				shouldBeBottom = false;
			}

			// Check if stuck (only if not at bottom)
			// We use a small tolerance (1px) because of sub-pixel rendering
			let shouldBeStuck = false;
			if (!shouldBeBottom && stickyContainerRef.current) {
				const stickyRect = stickyContainerRef.current.getBoundingClientRect();
				// It is stuck if its top position is at the sticky offset (stickyTop)
				shouldBeStuck = stickyRect.top <= stickyTop + 1;
			}

			setIsBottom((prev) => {
				if (prev !== shouldBeBottom) {
					// Capture previous rect before state update triggers re-render
					if (stickyContainerRef.current) {
						prevRectRef.current =
							stickyContainerRef.current.getBoundingClientRect();
					}
					return shouldBeBottom;
				}
				return prev;
			});

			setIsStuck(shouldBeStuck);
		};

		window.addEventListener('scroll', handleScroll, { passive: true });
		window.addEventListener('resize', handleScroll); // Handle window resize

		// Observe content size changes (e.g. images loading)
		const resizeObserver = new ResizeObserver(() => {
			handleScroll();
		});

		if (contentWrapperRef.current) {
			resizeObserver.observe(contentWrapperRef.current);
		}

		handleScroll(); // Check initial state

		return () => {
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', handleScroll);
			resizeObserver.disconnect();
		};
	}, [
		contentWrapperRef,
		stickyContainerRef,
		offset,
		stickyTop,
		forceSticky,
		isStabilizing,
	]);

	// 2. FLIP Animation
	useLayoutEffect(() => {
		if (
			!stickyContainerRef.current ||
			!prevRectRef.current ||
			!contentWrapperRef.current ||
			forceSticky ||
			isStabilizing
		)
			return;

		const el = stickyContainerRef.current;
		const startRect = prevRectRef.current;
		const endRect = el.getBoundingClientRect();

		const deltaY = startRect.top - endRect.top;

		if (deltaY !== 0) {
			// Invert
			el.style.transform = `translateY(${deltaY}px)`;
			el.style.transition = 'none';

			// Force reflow
			el.getBoundingClientRect();

			// Play
			el.style.transform = '';
			// Note: We don't set scale here, css transition handles scale if class changes
			el.style.transition = 'transform 0.6s cubic-bezier(0.25, 1, 0.5, 1)';
		}
	}, [isBottom, stickyContainerRef, contentWrapperRef]);

	return { isBottom, isStuck };
};
